import { Action, Module, Mutation, VuexModule } from 'vuex-module-decorators'
import { BandingSetupDTO } from '@core/pages/production-bonus-setup/api/models/BandingSetupDTO'
import { BandingSetupService } from '@core/pages/production-bonus-setup/api/services/BandingSetupService'
import { BandingSetupListRequestDto } from '@core/pages/production-bonus-setup/api/models/BandingSetupListRequestDto'
import { PersistencyPayoutRecordDTO, PersistencyPayoutUpdateRequestDTO } from '../api/models/PersistencyPayoutUpdateRequestDTO'
import { PersistencySetupService } from '../api/services/PersistencySetupService'
import { ProductionBonusService } from '../api/services/ProductionBonusService'
import { ProductionBonusSetupService } from '@core/pages/production-bonus-setup/api/services/ProductionBonusSetupService'

@Module({
  namespaced: true,
  name: 'ProductionBonusSetupStore'
})
export default class ProductionBonusSetupStore extends VuexModule {
  productionBonusSetupRows: BandingSetupDTO[] = []
  persistencyList: PersistencyPayoutRecordDTO[] = []
  nettCommissionReportList = []

  violationRecordsTotal = null
  violationData = null

  productionBonusInView: BandingSetupDTO = null
  public setProductionBonusSetupRecordsTotal = 0

  @Action({ rawError: true })
  async importBandingCsv(data) {
    const res = await BandingSetupService.bandingSetupImportCsv(data)
      return res
  }

  @Action({ rawError: true })
  async importViolationCsv(data) {
    const res = await ProductionBonusService.importViolation(data)
      return res
  }

  @Action({ rawError: true })
  async fetchBanindgList(body: BandingSetupListRequestDto) {
    try {
      const res = await BandingSetupService.getBandingSetupList(body)
      this.context.commit('setBandingList', res)
      return {
        success: true,
        data: res.data
      }
    } catch (error) {
      return {
        success: false,
        message: error.message || 'Failed to retrieve banding list.'
      }
    }
  }

  @Mutation
  setBandingList({ data, recordsTotal }) {
    this.productionBonusSetupRows = data ?? []
    this.setProductionBonusSetupRecordsTotal = recordsTotal
  }

  @Action({ rawError: true })
  async saveProdBonusBanding(payload) {
    const res = await BandingSetupService.saveBandingSetup(payload)
  }

  @Action({ rawError: true })
  async exportProductionBonusBanding() {
    return await BandingSetupService.exportCsvData()
  }

  @Action({ rawError: true })
  async exportViolations() {
    return await ProductionBonusService.exportViolation()
  }

  @Mutation
  setPersistencyList(data: PersistencyPayoutRecordDTO[]) {
    this.persistencyList = data ?? []
  }

  @Action({ rawError: true })
  async retrievePersistencyPayoutList() {
    try {
      const res = await PersistencySetupService.retreivePersistencyPayoutList()
      this.context.commit('setPersistencyList', res.data)
      return {
        success: true,
        data: res.data
      }
    } catch (error) {
      return {
        success: false,
        message: error.message || 'Failed to retrieve payout list.'
      }
    }
  }

  @Action({ rawError: true })
  async updatePersistencyPayout(payload: PersistencyPayoutUpdateRequestDTO) {
    try {
      const res = await PersistencySetupService.updatePersistencyPayout(payload)
      this.context.commit('setPersistencyList', res.data)
      return {
        success: true,
        message: res.message || 'Payout updated successfully.',
        data: res.data
      }
    } catch (error) {
      return {
        success: false,
        message: error.message || 'Failed to update payout.'
      }
    }
  }

  @Action({ rawError: true })
  async fetchViolations (body) {
    const res = await ProductionBonusService.retrieveListOfViolation(body)
    this.context.commit('setViolationsList', res)
  }

  @Action({ rawError: true })
  async createViolation (body) {
    const res = await ProductionBonusService.createViolation(body)
    return res;
  }

  @Action({ rawError: true })
  async deleteViolations(payload) {
    const res = await ProductionBonusService.deleteViolationRecord(payload)
    return res;
  }

  @Action({ rawError: true })
  async fetchNettCommissions(payload) {
    try {
      const res = await ProductionBonusService.retrieveListOfNettCommission(payload)
      this.setNettCommissionReportList(res.data)
      return {
        success: true,
        data: res.data
      }
    } catch (error) {
      return {
        success: false,
        message: error.message || 'Failed to retrieve NettCommission report list.'
      }
    }
  }

  @Action({ rawError: true })
  async publishNettCommission({ oid, payload }) {
    const { isPublished } = payload
    try {
      const res = await ProductionBonusService.publishNettCommission(oid, payload);
      this.context.commit('updateNettCommissionReport', { oid, payload })
      return {
        success: true,
        message: res.message || `Nett Commission report ${isPublished ? 'published' : 'unpublished'} successfully.`
      }
    } catch (error) {
      return {
        success: false,
        message: error.message || `Failed to ${isPublished ? 'publish' : 'unpublish'} report`
      }
    }
  }

  @Action({ rawError: true })
  async deleteNettCommissionReport(oid) {
    try {
      const res = await ProductionBonusService.deleteNettCommission(oid);
      this.context.commit('removeNettCommissionReport', oid)
      return {
        success: true,
        message: res.message || 'Nett Commission report deleted successfully'
      }
    } catch (error) {
      return {
        success: false,
        message: error.message || 'Failed to delete Nett Commission report'
      }
    }
  }

  @Mutation
   setViolationsList({ data, recordsTotal }) {
    this.violationData = data
    this.violationRecordsTotal = recordsTotal
   }

  @Action({ rawError: true })
  async importNettCommissionCsv(data) {
    const {oid, fileData} = data;
    const res = await ProductionBonusSetupService.importProductionBonusReportData(oid,fileData)
      return res
  }

  @Mutation
  setNettCommissionReportList(data) {
    this.nettCommissionReportList = data ?? [];
  }

  @Mutation
  updateNettCommissionReport({ oid, payload }) {
    this.nettCommissionReportList = this.nettCommissionReportList.map(report => report?.oid === oid ? { ...report, ...payload } : { ...report })
  }

  @Mutation
  removeNettCommissionReport(oid) {
    this.nettCommissionReportList = this.nettCommissionReportList.filter(report => report?.oid !== oid)
  }

  @Action({ rawError: true })
  async addNettCommissionReport(payload) {
    try {
      const res = await ProductionBonusService.createNettCommission(payload);
      return {
        success: true,
        message: res.message || 'Nett Commission report added successfully'
      }
    } catch (error) {
      return {
        success: false,
        message: error.message || 'Failed to add Nett Commission report'
      }
    }
  }

  @Action({ rawError: true })
  async updateNettCommission({ oid, payload }) {
    try {
      const res = await ProductionBonusService.updateNettCommission(oid, payload);
      this.context.commit('updateNettCommissionReport', { oid, payload })
      return {
        success: true,
        message: res.message || 'Nett Commission report updated successfully'
      }

    } catch (error) {
      return {
        success: false,
        message: error.message || 'Failed to update Nett Commission report'
      }
    }
  }

  @Action({ rawError: true })
  async deleteBandingRecord(payload) {
    const res = await BandingSetupService.deleteBandingRecord(payload);
    return res;
  }
  
  @Action({ rawError: true })
  async updateViolation(body) {
    const res = await ProductionBonusService.updateViolationRecord(body.oid, body);
  }
}